import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, Upload, AlertCircle, FileText, Users, Edit, Trash2, Eye, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { leadsListService } from '../lib/supabaseServices';
import LeadsImportModal from '../components/LeadsImportModal';
import LeadsListManagementModal from '../components/LeadsListManagementModal';
import LeadsListViewer from '../components/LeadsListViewer';

const LeadsPage = ({ darkMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [leadsLists, setLeadsLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listToDelete, setListToDelete] = useState(null);

  // Load leads lists on component mount
  useEffect(() => {
    loadLeadsLists();
  }, []);

  const loadLeadsLists = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add minimum loading time for smooth transitions
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 800));
      const dataPromise = leadsListService.getLeadsLists();
      
      const [data] = await Promise.all([dataPromise, minLoadTime]);
      setLeadsLists(data);
    } catch (err) {
      console.error('Error loading leads lists:', err);
      setError(err.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  };

  const handleLeadsListCreated = async (newList) => {
    await loadLeadsLists(); // Refresh the list
    setShowImportModal(false);
  };

  const handleEditList = (list) => {
    setSelectedList(list);
    setShowEditModal(true);
  };

  const handleViewList = (list) => {
    setSelectedList(list);
    setShowViewModal(true);
  };

  const handleLeadsListUpdated = async (listId) => {
    await loadLeadsLists(); // Refresh the list
    setShowEditModal(false);
    setSelectedList(null);
  };

  const handleDeleteList = (list) => {
    setListToDelete(list);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await leadsListService.deleteLeadsList(listToDelete.id);
      await loadLeadsLists(); // Refresh the list
      setShowDeleteModal(false);
      setListToDelete(null);
    } catch (err) {
      console.error('Error deleting leads list:', err);
      setError('Failed to delete leads list');
    }
  };

  const filteredLeadsLists = leadsLists.filter(list =>
    list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (list.description && list.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 lg:p-6"
    >
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-3xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-2`}>
          Leads Lists
        </h1>
        <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
          Manage your imported leads lists and use them across campaigns
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col space-y-4 mb-8 lg:flex-row lg:justify-between lg:items-center lg:space-y-0">
        <div className="relative max-w-md">
          <Search className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-secondary' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Search leads lists..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 pr-4 py-3 w-full border rounded focus:ring-2 focus:border-transparent transition-all duration-200 ${
              darkMode 
                ? 'bg-surface/50 border-border text-primary placeholder-secondary focus:ring-accent focus:bg-surface' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:shadow-lg'
            }`}
          />
        </div>
        <button 
          onClick={() => setShowImportModal(true)}
          className={`${
            darkMode 
              ? 'bg-gradient-to-r from-accent to-accent-hover shadow-lg shadow-accent/25 hover:shadow-accent/40' 
              : 'bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg hover:shadow-xl'
          } text-primary px-8 py-3 rounded font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2`}
        >
          <Plus className="w-5 h-5" />
          <span>Import Leads List</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mb-6 p-4 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded flex items-center space-x-2`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border animate-pulse`}>
              <div className={`w-12 h-12 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-4`}></div>
              <div className={`w-32 h-6 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-2`}></div>
              <div className={`w-full h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-4`}></div>
              <div className="flex justify-between items-center">
                <div className={`w-16 h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                <div className={`w-20 h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredLeadsLists.length === 0 ? (
        <div className={`text-center py-12 ${darkMode ? 'text-secondary' : 'text-gray-500'}`}>
          <div className={`w-16 h-16 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <FileText className="w-8 h-8" />
          </div>
          <p className="text-lg font-medium mb-2">
            {searchTerm ? 'No leads lists found' : 'No leads lists yet'}
          </p>
          <p className="mb-4">
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Import your first CSV file with Instagram usernames to get started'
            }
          </p>
          {!searchTerm && (
            <button 
              onClick={() => setShowImportModal(true)}
              className={`${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-blue-500 to-purple-600'} text-primary px-6 py-3 rounded font-semibold hover:shadow-lg transition-all duration-300`}
            >
              Import Your First List
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLeadsLists.map((list) => (
            <motion.div
              key={list.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`${
                darkMode 
                  ? 'bg-gradient-to-br from-surface to-elevated border-border shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/40 hover:border-border' 
                  : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg hover:shadow-xl hover:shadow-gray-900/10 hover:border-gray-300'
              } rounded p-4 lg:p-5 border transition-all duration-300 ease-out group`}
            >
              {/* Header: icon + name inline */}
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 shrink-0 ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-blue-500 to-purple-600'} rounded flex items-center justify-center shadow`}>
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className={`text-lg font-bold leading-tight ${darkMode ? 'text-primary' : 'text-gray-900'} line-clamp-2`}>
                    {list.name}
                  </h3>
                  {list.description && (
                    <p className={`text-sm mt-1 ${darkMode ? 'text-secondary' : 'text-gray-600'} line-clamp-2`}>
                      {list.description}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Key Metrics */}
              {/* Total Leads */}
              <div className={`flex items-center justify-between mb-3 p-3 rounded ${darkMode ? 'bg-elevated/30' : 'bg-gray-50'}`}>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-accent" />
                  <span className={`text-xs font-medium ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>Total Leads</span>
                </div>
                <span className={`text-xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                  {list.total_leads?.toLocaleString() || 0}
                </span>
              </div>

              {/* Usage Status */}
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'}`}>List Status</span>
                <div className="flex items-center space-x-1.5">
                  <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-emerald-400' : 'bg-green-500'}`}></div>
                  <span className={`text-xs font-medium ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>Active</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-opacity-10 border-gray-500">
                <div className="flex items-center space-x-2">
                  <Calendar className={`w-4 h-4 ${darkMode ? 'text-secondary' : 'text-gray-500'}`} />
                  <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-500'}`}>
                    {format(new Date(list.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => handleViewList(list)}
                    className={`p-2 rounded transition-all duration-200 ${
                      darkMode 
                        ? 'text-secondary hover:bg-elevated/20 hover:text-primary hover:scale-110' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-700 hover:scale-110'
                    }`}
                    title="View Leads"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleEditList(list)}
                    className={`p-2 rounded transition-all duration-200 ${
                      darkMode 
                        ? 'text-accent hover:bg-accent/20 hover:text-accent hover:scale-110' 
                        : 'text-accent hover:bg-accent/10 hover:text-accent hover:scale-110'
                    }`}
                    title="Edit List"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDeleteList(list)}
                    className={`p-2 rounded transition-all duration-200 ${
                      darkMode 
                        ? 'text-red-400 hover:bg-red-500/20 hover:text-red-300 hover:scale-110' 
                        : 'text-red-600 hover:bg-red-50 hover:text-red-700 hover:scale-110'
                    }`}
                    title="Delete List"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Leads Import Modal */}
      <LeadsImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onLeadsListSelected={handleLeadsListCreated}
        darkMode={darkMode}
      />

      {/* Leads List Management Modal */}
      <LeadsListManagementModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        darkMode={darkMode}
        leadsList={selectedList}
        onLeadsListUpdated={handleLeadsListUpdated}
      />

      {/* Leads List Viewer Modal */}
      <LeadsListViewer
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        darkMode={darkMode}
        leadsList={selectedList}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded shadow-2xl border max-w-md w-full p-6`}>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-2`}>
              Delete Leads List
            </h3>
            <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'} mb-6`}>
              Are you sure you want to delete "{listToDelete?.name}"? This action cannot be undone and will remove all {listToDelete?.total_leads} leads from this list.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`flex-1 px-4 py-2 ${darkMode ? 'bg-elevated hover:bg-surface text-secondary' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-primary rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default LeadsPage;